import { useState } from "react";
import styling from "../dashboards/styles/NewDecisionTree";
import { questionsConfig } from "../../helpers/new_ques";
import { useTranslation } from "react-i18next";
import {
  filterDataByField,
  filterDataByMultipleFields,
  filterDataByOccurrence,
} from "../../utils/descisionTreeUtils";
import { Box, Button} from "@mui/material";
import ArrowRight from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";

import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";

import { getLastKey } from "../../utils/formUtils";
import { MyLocation } from "@mui/icons-material";

// Define styles for scrolling and button spacing (can be added to your existing 'styling' object or component scope)
const SCROLLABLE_OPTIONS_SX = {
  minHeight: 0, 
  maxHeight: '100%', 
  flexGrow: 1,      
  overflowY: 'auto', 
  paddingRight: '10px',
};

const SKIP_BUTTON_WRAPPER_SX = {
  width: '100%',
  // Reserve vertical space equal to the button height + margin
  minHeight: '40px', 
  marginTop: '8px', // Adjust margin to match expected spacing
};

export function Questions({
  selectedQuestionId,
  handleQuestionClick,
  questions,
}) {
  const unansweredQuestion = questions.find((q) => !q.answered);

  return (
    <div style={styling.secondFilterSection}>
      {questions.map((question) => {
        if (question.answered || question.id === unansweredQuestion.id) {
          return (
            <Filter
              key={question.id}
              selectedQuestion={selectedQuestionId === question.id}
              answered={question.answered}
              handleQuestionClick={() => handleQuestionClick(question.id)}
              question={question}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

let filteredData = [];

export function QuestionSelection({
  array,
  selectedOptions,
  onSelect,
  currentField,
  questionsResults,
  currentQuestionId,
  category,
}) {
  const { t } = useTranslation();
  const selectedOption = selectedOptions[currentQuestionId];

  const questions = category
    ? questionsConfig[category].map((q) => ({ ...q, text: t(q.text) }))
    : [];
  const currentQuestion = questions.find((q) => q.id === currentQuestionId);

  const handleSkipClick = () => {
    const newOption =
      selectedOption?.option?.value === "Skip"
        ? null
        : { option: { label: "Skip", value: "Skip" } };
  onSelect(newOption);
};

  const handleRadioChange = (option) => {
    onSelect({ option });
  };

  const isOptionDisabled = (optionValue) => {
    if (!questionsResults || !currentQuestionId || currentQuestionId === 1) {
      return false;
    }

    if (isAnswerStillValid(selectedOption, array)) {
      return false;
    }

    const relatedColumns = Array.isArray(optionValue)
      ? optionValue
      : [optionValue];

    try {
      const prevResults = questionsResults[getLastKey(questionsResults)];

      if (currentQuestion.isFilterByOccurrence) {
        filteredData = filterDataByOccurrence(
          prevResults,
          currentQuestion.valueFields,
          relatedColumns
        );
      } else if (currentQuestion.isComplexFilter) {
        filteredData = filterDataByMultipleFields(
          prevResults,
          currentQuestion.properties,
          relatedColumns,
          currentQuestion.propertyFields,
          currentQuestion.valueFields
        );
      } else {
        filteredData = filterDataByField(
          prevResults,
          currentField,
          relatedColumns
        );
      }

      if (selectedOptions[currentQuestionId - 1].option.value === "Skip") {
        if (filteredData.length === 0) {
          return true;
        }
        return false;
      }

      return filteredData.length === 0;
    } catch (error) {
      console.error("Error checking option availability:", error);
      return false;
    }
  };

  const renderQuestionOptions = () => {
    return array.map((option, index) => {
      return (
        <FormControlLabel
          key={index}
          value={option.value}
          control={
            <Radio
              sx={styling.radioButtonStyling}
              checked={selectedOption?.option?.value === option.value}
              onChange={() => {
                handleRadioChange(option);
              }}
              disabled={isOptionDisabled(option.value)}
            />
          }
          label={t(`dashboard.decision-tree.options.${option.translationKey}`, {
            defaultValue: option.label,
          })}
        />
      );
    });
  };

  const renderFetchedOptions = () => {
    return array?.options?.map((option, index) => {
      return (
        <FormControlLabel
          key={index}
          value={option}
          control={
            <Radio
              sx={styling.radioButtonStyling}
              checked={selectedOption?.option?.value === option}
              onChange={() =>
                handleRadioChange({ label: option, value: option })
              }
              disabled={isOptionDisabled(option)}
            />
          }
          label={option}
        />
      );
    });
  };

  return (
    <div style={styling.thirdFilterSection}> 
      <FormControl sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flexGrow: 1,
        minHeight: 0,
        marginBottom: 0,
        }}>
        <FormLabel sx={styling.formLabel}> Options </FormLabel>
        
        <Box sx={SCROLLABLE_OPTIONS_SX}> 
          <RadioGroup
            sx={styling.radioGroup}
            value={
              selectedOption?.option?.value === "Skip"
                ? null
                : selectedOption?.option?.value
            }
          >
            {/* Options rendering */}
            {array && array.length > 0
              ? renderQuestionOptions()
              : renderFetchedOptions()}
          </RadioGroup>
        </Box>
      </FormControl>

      <Box sx={SKIP_BUTTON_WRAPPER_SX}> 
        {(array.length > 0 || array?.options?.length) && (
          <Button
            variant="contained"
            color="success"
            size="medium"
            onClick={handleSkipClick}
            sx={{
              padding: "4px",
              width: "100%", 
            }}
            disabled={selectedOption?.option?.value === "Skip"}
          >
            Skip
          </Button>
        )}
      </Box>
    </div>
  );
}

// ============================ COMPONENTS ============================

function Filter({ selectedQuestion, answered, handleQuestionClick, question }) {
  return (
    <Button
      style={{
        color: selectedQuestion ? "#ab6e50" : answered ? "#4caf50" : "#263828",
      }}
      endIcon={
        !answered ? (
          <ArrowRight
            style={{
              transition: "transform 0.2s",
              transform: selectedQuestion ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        ) : (
          <CheckIcon />
        )
      }
      onClick={handleQuestionClick}
    >
      {question.text}
    </Button>
  );
}

const isAnswerStillValid = (selectedOption, array) => {
  const selectedValue = selectedOption?.option?.value;

  if (!selectedValue) return false;

  const optionValues =
    array?.length > 0 ? array.map((o) => o.value) : array?.options || [];

  return optionValues.includes(selectedValue);
};
